import { Controller } from "@hotwired/stimulus"

const DEFAULTS = {
  pitch: 54,
  pace: 48,
  grit: 46,
  echo: 38,
  crush: 42,
  drive: 52
}

export default class extends Controller {
  static targets = [
    "slider",
    "readout",
    "meter",
    "waveBar",
    "copyInput",
    "copyCount",
    "previewButton",
    "previewStatus",
    "previewAudio",
    "sampleInput",
    "sampleLabel",
    "baseVoiceInput",
    "providerSelect",
    "providerFields",
    "providerVoiceId",
    "voiceCard",
    "filterOutput"
  ]

  static values = {
    previewUrl: String
  }

  connect() {
    this.baseVoice = this.initialVoice()
    this.selectVoice(this.baseVoice)
    this.copyChanged()
    this.providerChanged()
    this.tune()
  }

  chooseVoice(event) {
    const button = event.currentTarget
    const voice = button.dataset.voice
    if (!voice) return

    this.selectVoice(voice)
    this.pulse()
  }

  async test(event) {
    event.preventDefault()
    if (!this.hasPreviewUrlValue || !this.baseVoice) return

    const button = this.hasPreviewButtonTarget ? this.previewButtonTarget : event.currentTarget
    const formData = new FormData()
    formData.append("base_voice", this.baseVoice)
    formData.append("provider", this.selectedProvider())
    if (this.selectedProvider() === "elevenlabs" && this.hasProviderVoiceIdTarget) {
      formData.append("provider_voice_id", this.providerVoiceIdTarget.value.trim())
    }
    formData.append("text", this.copyText())

    Object.entries(this.valuesFromSliders()).forEach(([key, value]) => {
      formData.append(`settings[${key}]`, value)
    })

    if (button) button.disabled = true
    this.setStatus("rendering preview...")

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "application/json"
        },
        body: formData
      })
      const data = await response.json()

      if (!response.ok || !data.url) {
        this.setStatus(data.error || "preview failed")
        return
      }

      if (this.hasPreviewAudioTarget) {
        this.previewAudioTarget.src = `${data.url}?v=${Date.now()}`
        this.previewAudioTarget.hidden = false
        await this.previewAudioTarget.play().catch(() => {})
      }

      if (this.hasSampleInputTarget) this.sampleInputTarget.value = data.url
      this.setStatus("preview ready")
      this.animateWave()
    } catch (_error) {
      this.setStatus("preview failed")
    } finally {
      if (button) button.disabled = false
    }
  }

  copyChanged() {
    if (!this.hasCopyInputTarget) return

    if (this.copyInputTarget.value.length > 99) {
      this.copyInputTarget.value = this.copyInputTarget.value.slice(0, 99)
    }

    if (this.hasCopyCountTarget) {
      this.copyCountTarget.textContent = `${this.copyInputTarget.value.length}/99`
    }
  }

  tune() {
    const values = this.valuesFromSliders()

    this.readoutTargets.forEach((target) => {
      const key = target.dataset.key
      target.textContent = values[key] ?? 0
    })

    this.meterTargets.forEach((target) => {
      const key = target.dataset.key
      const value = values[key] ?? 0
      target.style.setProperty("--level", value)
      target.style.filter = `brightness(${1 + (value / 170)})`
    })

    this.paintWave(values)

    if (this.hasFilterOutputTarget) {
      this.filterOutputTarget.textContent = this.filterPreview(values)
    }
  }

  randomize() {
    this.sliderTargets.forEach((slider) => {
      slider.value = String(18 + Math.floor(Math.random() * 70))
    })
    this.tune()
    this.pulse()
  }

  reset() {
    this.sliderTargets.forEach((slider) => {
      const key = slider.dataset.key
      slider.value = String(DEFAULTS[key] ?? 50)
    })
    this.tune()
  }

  selectVoice(voice) {
    this.baseVoice = voice
    if (this.hasBaseVoiceInputTarget) this.baseVoiceInputTarget.value = voice
    if (this.hasProviderSelectTarget) {
      this.providerSelectTarget.value = "local"
      this.providerChanged()
    }

    let selectedName = ""
    let selectedSample = ""
    this.voiceCardTargets.forEach((card) => {
      const selected = card.dataset.voice === voice
      card.classList.toggle("is-selected", selected)
      card.setAttribute("aria-pressed", selected ? "true" : "false")
      if (selected) {
        selectedName = card.dataset.voiceName || voice
        selectedSample = card.dataset.sampleUrl || ""
      }
    })

    if (this.hasSampleInputTarget && selectedSample) this.sampleInputTarget.value = selectedSample
    if (this.hasSampleLabelTarget) this.sampleLabelTarget.textContent = selectedName || voice
    this.setStatus(`${selectedName || voice} loaded`)
  }

  initialVoice() {
    const saved = this.hasBaseVoiceInputTarget ? this.baseVoiceInputTarget.value : ""
    if (saved) return saved
    return this.voiceCardTargets[0]?.dataset.voice || ""
  }

  providerChanged() {
    const external = this.selectedProvider() === "elevenlabs"
    if (this.hasProviderFieldsTarget) this.providerFieldsTarget.hidden = !external
    if (this.hasProviderVoiceIdTarget) this.providerVoiceIdTarget.required = external
    this.setStatus(external ? "ElevenLabs selected // clicking test spends your provider credits" : "local Coqui loaded")
  }

  selectedProvider() {
    return this.hasProviderSelectTarget ? this.providerSelectTarget.value : "local"
  }

  valuesFromSliders() {
    return this.sliderTargets.reduce((memo, slider) => {
      memo[slider.dataset.key] = Number.parseInt(slider.value, 10) || 0
      return memo
    }, {})
  }

  copyText() {
    const value = this.hasCopyInputTarget ? this.copyInputTarget.value.trim() : ""
    return (value || "Alice sends clean signal.").slice(0, 99)
  }

  paintWave(values = this.valuesFromSliders()) {
    const pitch = values.pitch || 50
    const pace = values.pace || 50
    const grit = values.grit || 50
    const echo = values.echo || 50
    const drive = values.drive || 50

    this.waveBarTargets.forEach((bar, index) => {
      const phase = index / Math.max(1, this.waveBarTargets.length - 1)
      const wave = Math.sin((phase * Math.PI * 2) + (pace / 20))
      const pulse = Math.cos((phase * Math.PI * 4) + (echo / 22))
      const height = 18 + ((wave + 1) * 18) + ((pulse + 1) * 9) + (drive * 0.22) + ((pitch - 50) * 0.08)
      bar.style.height = `${Math.max(10, Math.min(96, height))}%`
      bar.style.opacity = `${0.48 + (grit / 210)}`
    })
  }

  animateWave() {
    this.waveBarTargets.forEach((bar, index) => {
      bar.animate(
        [
          { transform: "scaleY(0.72)" },
          { transform: "scaleY(1.2)" },
          { transform: "scaleY(1)" }
        ],
        { duration: 420 + (index * 12), easing: "steps(4, end)" }
      )
    })
  }

  filterPreview(values) {
    const pitch = this.scale(values.pitch, 0.46, 0.92).toFixed(2)
    const speed = this.scale(values.pace, 0.82, 1.38).toFixed(2)
    const bits = Math.max(5, Math.min(12, Math.round(12 - ((values.crush || 0) / 100 * 7))))
    const echoDelay = Math.round(this.scale(values.echo, 18, 112))
    const volume = this.scale(values.drive, 0.96, 1.26).toFixed(2)

    return `pitch ${pitch} // speed ${speed} // bits ${bits} // echo ${echoDelay} // drive ${volume}`
  }

  scale(value, low, high) {
    const clamped = Math.max(0, Math.min(100, Number(value) || 0))
    return low + ((clamped / 100) * (high - low))
  }

  pulse() {
    this.animateWave()
  }

  setStatus(text) {
    if (this.hasPreviewStatusTarget) this.previewStatusTarget.textContent = text || ""
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
